import { useState } from "react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CancelReasonDialog } from "@/features/staff/cancel-reason-dialog"
import {
  INVENTORY_PATHS,
  adjustmentRequestComplete,
  tInventory,
  type AdjustmentReasonOption,
  type Supply,
} from "@/features/staff/inventory/inventory"

/**
 * Writing waste, theft, a transfer or a count correction into the ledger.
 *
 * ## It is the cancellation dialog, on purpose
 *
 * This asks the identical question a cancellation asks — *why did this happen,
 * from a list nobody may add to* — so it mounts `CancelReasonDialog` with its
 * own copy rather than a second dialog. Two dialogs would mean two places
 * deciding when the confirm button unlocks and whether Escape may close a
 * request mid-flight, and the second one would learn about the first's fixes
 * late. The quantity and the note ride in as `children`; `ready` is what tells
 * the shared confirm button that they are filled in.
 *
 * ## One input, two questions, and the reason decides which
 *
 * For `waste`, `theft` and `transfer_out` the operator knows how much LEFT and
 * types a positive number the server subtracts. For `count_correction` they know
 * what IS THERE — they just counted the shelf — and the server writes the
 * difference. Asking for a delta there would push the subtraction into somebody's
 * head at the end of a shift, which is the arithmetic this whole feature exists
 * to stop trusting. `counted` arrives on the reason option so the label, the hint
 * and the current balance all change together.
 *
 * ## Nothing here edits anything
 *
 * This POSTs a new row. There is no edit path and no delete path for a movement
 * anywhere in this feature: the ledger's rows are `readonly?`, and the answer to
 * a movement written wrong is another movement — which is exactly what a count
 * correction is. The dialog says so in its own description rather than leaving
 * the operator to discover it by looking for a pencil that is not there.
 */
export function StockAdjustmentDialog({
  supply,
  reasons,
}: {
  supply: Supply
  reasons: AdjustmentReasonOption[]
}) {
  const [reason, setReason] = useState("")
  const [quantity, setQuantity] = useState("")
  const [note, setNote] = useState("")

  const selected = reasons.find((option) => option.value === reason)
  const counted = selected?.counted ?? false

  const reset = () => {
    setReason("")
    setQuantity("")
    setNote("")
  }

  return (
    <CancelReasonDialog
      path={INVENTORY_PATHS.adjustments(supply.id)}
      method="post"
      reasons={reasons}
      payload={(chosen) => ({ reason: chosen, quantity: quantity.trim(), note: note.trim() })}
      triggerLabel={tInventory("adjust_open")}
      triggerVariant="default"
      triggerSize="default"
      triggerTestId="stock-adjustment-open"
      testIdPrefix="stock-adjustment"
      confirmVariant="default"
      copy={{
        title: tInventory("adjust_title"),
        description: tInventory("adjust_description"),
        reasonLabel: tInventory("adjust_reason_label"),
        reasonPlaceholder: tInventory("adjust_reason_placeholder"),
        cancel: tInventory("adjust_cancel"),
        confirm: tInventory("adjust_confirm"),
        submitting: tInventory("adjust_submitting"),
      }}
      ready={adjustmentRequestComplete({ quantity, note, reason, reasons })}
      onReasonChange={setReason}
      onReset={reset}
    >
      <div className="space-y-2">
        <Label htmlFor="stock-adjustment-quantity">
          {counted
            ? tInventory("adjust_quantity_counted_label")
            : tInventory("adjust_quantity_removed_label")}{" "}
          <span className="text-muted-foreground">({supply.unitLabel})</span>
        </Label>
        <Input
          id="stock-adjustment-quantity"
          inputMode="decimal"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          data-testid="stock-adjustment-quantity"
        />
        <p className="text-muted-foreground text-xs">
          {counted
            ? tInventory("adjust_quantity_counted_hint", {
                balance: `${supply.stockOnHand} ${supply.unitLabel}`,
              })
            : tInventory("adjust_quantity_removed_hint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="stock-adjustment-note">{tInventory("adjust_note_label")}</Label>
        <Input
          id="stock-adjustment-note"
          value={note}
          placeholder={tInventory("adjust_note_placeholder")}
          onChange={(event) => setNote(event.target.value)}
          data-testid="stock-adjustment-note"
        />
        {selected?.noteRequired && (
          <p className="text-muted-foreground text-xs" data-testid="stock-adjustment-note-required">
            {tInventory("adjust_note_required")}
          </p>
        )}
      </div>
    </CancelReasonDialog>
  )
}
